'use client';

import { useState } from 'react';

import { Flex, Skeleton, Spacer, Stack, toast } from '@backyard-ui/core';

import { Services } from '@/infra/services';
import { algolia } from '@/infra/tracking/algolia';
import {
  Counter,
  type OnChangeCounter,
} from '@/presenters/components/common/Counter';
import { useCrossesServices } from '@/presenters/hooks/catalog/use-crosses-services';
import { useUserResources } from '@/presenters/hooks/user';
import { useProductStore } from '@/presenters/store/catalog/product';

import { PaymentMethodsModal } from '../../PaymentMethodsModal';
import { PriceTag } from '../../PriceTag';

import { BuyButtons } from './BuyButtons';
import { RestrictionMessage } from './RestrictionMessage';
import { Sellers } from './Sellers';
import { ServiceOfferCard } from './ServiceOfferCard';
import { Shipments } from './Shipments';
import { StoreStockDrawer } from './StoreStockDrawer';
import { Variants } from './Variants';

type BuyBoxProps = {
  itemId: number;
};

export default function BuyBox(props: Readonly<BuyBoxProps>) {
  const { itemId } = props;

  const {
    isOutlet,
    pricing,
    shop,
    id,
    ecommerceScopes: { canPurchase, canPickupInStore },
    isLMFreightActive,
  } = useProductStore((store) => store.currentSeller);

  const {
    onDemand,
    unit,
    pack,
    _id: productId,
  } = useProductStore((store) => store.product);

  const { data: isLegalUser } = useUserResources((data) => data.isLegalUser);

  const { data: crossesServices, isFetching } = useCrossesServices(itemId);

  const [quantity, setQuantity] = useState(pack.packaging);
  const [unitQuantity, setUnitQuantity] = useState(1);
  const [selectedService, setSelectedService] = useState<{
    id: number;
    isSelected: boolean;
  } | null>(null);

  const onChangeCounter: OnChangeCounter = ({ quantity, unitQuantity }) => {
    setQuantity(quantity);
    setUnitQuantity(unitQuantity);
  };

  const handleServiceSelection = (serviceId: number, isSelected: boolean) => {
    setSelectedService(isSelected ? { id: serviceId, isSelected: true } : null);
  };

  const renderServiceOffer = () => {
    const isLeroy = id === '';
    const hasCrossesServices =
      crossesServices && crossesServices.data.length > 0 && isLeroy;

    if (isFetching)
      return <Skeleton height="120px" data-testid="service-offer-skeleton" />;
    if (!hasCrossesServices) return null;

    const service = crossesServices.data[0];
    const isServiceSelected =
      selectedService?.id === service.id && selectedService?.isSelected;

    return (
      <ServiceOfferCard
        data={service}
        isSelected={isServiceSelected}
        onSelectionChange={(isSelected) =>
          handleServiceSelection(service.id, isSelected)
        }
      />
    );
  };

  return (
    <Stack direction="column" align="flex-start" spacing="6">
      <Flex direction="column" align="flex-start" className="w-full">
        <PriceTag
          pricing={pricing}
          unit={unit}
          isOutlet={isOutlet}
          onDemand={onDemand}
          shop={shop}
          isLMFreightActive={isLMFreightActive}
          pack={pack}
          isSoldOut={!canPurchase && !canPickupInStore}
        />
        <Spacer size="2" axis="vertical" />
        <PaymentMethodsModal currentSellerId={shop.id} />
      </Flex>

      <Counter
        multiplier={pack.packaging}
        onChange={onChangeCounter}
        onClick={() => algolia.sendPdpClickEvent()}
        quantity={quantity}
        maxQuantity={
          Number.MAX_SAFE_INTEGER /** Precisamos da informação de stock */
        }
      />

      <Variants itemId={itemId} />

      <BuyButtons
        hasFloatingButton
        direction={{ initial: 'column', sm: 'row' }}
        quantity={unitQuantity}
        selectedService={selectedService}
        serviceData={crossesServices?.data?.[0]}
        onSuccess={() => {
          setQuantity(pack.packaging);
        }}
        postSuccess={async (data) => {
          if (selectedService?.isSelected) {
            const addedProduct = data.data.shippings?.[0]?.items?.find(
              (item) => {
                return item.productId === productId;
              }
            );

            if (addedProduct) {
              const servicePayload = {
                services: [
                  {
                    id: selectedService.id,
                    skuId: null,
                    quantity: addedProduct.quantity,
                    relatedId: addedProduct.id,
                  },
                ],
                override: true,
              };

              await Services.Checkout.Cart.addServicesToCart(servicePayload);
              toast({
                description: 'Serviço adicionado ao carrinho',
                appearance: 'success',
              });
              setSelectedService(null);
            }
          }
        }}
      />

      {renderServiceOffer()}

      {isLegalUser && <RestrictionMessage />}

      <Shipments />

      <StoreStockDrawer />
      <Sellers itemId={itemId} />
    </Stack>
  );
}
